import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Lock, BookOpen } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";

const EditDetails = ({type}) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const { id } = useParams();

  useEffect(() => {
    const userData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/admin/get-user/`,
          { userID: id, type: type }
        );

        if (response.status === 200) {
          const user = response.data.user;
          const userCourses = user.courses || [];
          setSelectedCourses(userCourses);

          // Use `setValue` to update the selected courses
          setValue("courses", userCourses);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    userData();
  }, [id, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/course/get-all-course-ids`,
        { withCredentials: true }
      );
      setAvailableCourses(result.data.courses);
    };
    fetchData();
  }, []);

  const onSubmit = async (data) => {
    console.log("Form Data:", { ...data, courses: selectedCourses });
    if (data?.password?.length > 6) {   
      const changePassword = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/change-password`,
        { userID: id, password: data.password, type: type },
        { withCredentials: true }
      );
      if(changePassword.status !== 200){
        alert("Error changing password");
      }
    }

    if (selectedCourses.length > 0) {
      const assignCourses = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/assign-courses`,
        { userID: id, type: type, courses: selectedCourses },
        { withCredentials: true }
      );
      if(assignCourses.status !== 200){
        alert("Error assigning courses");
      }
    }

    alert("User details updated successfully");
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleCourseChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setSelectedCourses([...selectedCourses, value]);
    } else {
      setSelectedCourses(selectedCourses.filter((course) => course !== value));
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-2xl rounded-2xl overflow-hidden border border-blue-100">
        <div className="bg-gray-800 text-white p-5 flex items-center justify-center space-x-3">
          <BookOpen className="w-7 h-7" />
          <h2 className="text-2xl font-bold">Edit User Details</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* User ID */}
          <div className="relative">
            <label
              htmlFor="id"
              className="text-gray-700 font-medium mb-2 flex items-center"
            >
              <Lock className="mr-2 text-gray-500" size={20} />
              User ID
            </label>
            <input
              id="id"
              type="text"
              value={id}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Password Update Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="changePassword"
              onChange={() => setShowPasswordFields(!showPasswordFields)}
              className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="changePassword"
              className="text-gray-700 font-medium"
            >
              Change Password
            </label>
          </div>

          {showPasswordFields && (
            <div className="space-y-4">
              {/* New Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="text-gray-700 font-medium mb-2 flex items-center"
                >
                  <Lock className="mr-2 text-gray-500" size={20} />
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters long",
                      },
                    })}
                    placeholder="Enter new password"
                    className={`w-full p-3 pr-12 border ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 w-12 bg-white px-3 flex items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className=" text-gray-700 font-medium mb-2 flex items-center"
                >
                  <Lock className="mr-2 text-gray-500" size={20} />
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (value) =>
                        value === watch("password") || "Passwords do not match",
                    })}
                    placeholder="Confirm new password"
                    className={`w-full p-3 pr-12 border ${
                      errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 px-3 w-12 bg-white flex items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Courses Selection */}
          <div>
            <label className="text-gray-700 font-medium mb-2 flex items-center">
              <BookOpen className="mr-2 text-gray-500" size={20} />
              Courses
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-44 overflow-y-auto bg-gray-50">
              {availableCourses.map((course) => (
                <div key={course} className="flex items-center mb-2 last:mb-0">
                  <input
                    type="checkbox"
                    id={course}
                    value={course}
                    onChange={handleCourseChange}
                    checked={selectedCourses.includes(course)}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={course} className="text-gray-700 flex-grow">
                    {course}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full p-3 bg-green-600 text-white font-semibold rounded-lg 
                        hover:bg-green-700 transition duration-300 
                        transform hover:scale-105 active:scale-95"
          >
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditDetails;
